import React, { useEffect, useState } from "react";
import { api, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { Users, Settings as SettingsIcon, Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Settings() {
  const { user } = useAuth();
  const t = useT();
  const [users, setUsers] = useState([]);
  const isAdmin = user?.role === "super_admin";
  const canEditHolidays = ["super_admin", "owner_director", "operations_admin"].includes(user?.role);

  useEffect(() => { (async () => {
    const { data } = await api.get("/users"); setUsers(data);
  })(); }, []);

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      const { data } = await api.get("/users"); setUsers(data);
      toast.success(`${u.name} ${u.is_active ? "deactivated" : "activated"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}`, { role });
      const { data } = await api.get("/users"); setUsers(data);
      toast.success(`${u.name} → ${ROLE_LABELS[role]}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("settings.subtitle")}</div>
        <h2 className="font-display text-2xl font-bold tracking-tight">{t("settings.title")}</h2>
      </div>

      <div className="ledger-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <SettingsIcon className="w-4 h-4 text-[#4DA6FF]" />
          <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("settings.agreement")}</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          <Param label={t("settings.param.split")} value={t("settings.param.splitValue")} />
          <Param label={t("settings.param.season")} value={t("settings.param.seasonValue")} />
          <Param label={t("settings.param.protected")} value={t("settings.param.protectedValue")} />
          <Param label={t("settings.param.approval")} value={t("settings.param.approvalValue")} />
          <Param label={t("settings.param.bareboat")} value={t("settings.param.bareboatValue")} tone="red" />
          <Param label={t("settings.param.excursion")} value={t("settings.param.excursionValue")} />
          <Param label={t("settings.param.deciding")} value={t("settings.param.decidingValue")} />
          <Param label={t("settings.param.holiday")} value={t("settings.param.holidayValue")} />
        </div>
      </div>

      <HolidayRotationSection users={users} canEdit={canEditHolidays} t={t} />

      <div className="ledger-card p-5" data-testid="users-table">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-4 h-4 text-[#A78BFA]" />
          <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("settings.team", { count: users.length })}</div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="font-mono text-[10px] tracking-widest text-[#94A3B8] border-b border-[#1E293B]">
              <th className="text-left py-2">{t("settings.col.name")}</th>
              <th className="text-left py-2 hidden md:table-cell">{t("settings.col.email")}</th>
              <th className="text-left py-2">{t("settings.col.role")}</th>
              <th className="text-right py-2">{t("settings.col.status")}</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-b border-[#1E293B]" data-testid={`user-row-${u.id}`}>
                <td className="py-3 font-medium">{u.name}</td>
                <td className="py-3 hidden md:table-cell text-[#94A3B8] font-mono text-xs">{u.email}</td>
                <td className="py-3">
                  {isAdmin ? (
                    <select value={u.role} onChange={(e) => changeRole(u, e.target.value)}
                      data-testid={`user-role-${u.id}`}
                      className="bg-[#050A14] border border-[#1E293B] rounded-md px-2 py-1 text-xs">
                      {Object.keys(ROLE_LABELS).map((k) => <option key={k} value={k}>{t(`role.${k}`)}</option>)}
                    </select>
                  ) : (
                    <span className="badge-pill badge-neutral">{t(`role.${u.role}`) || u.role}</span>
                  )}
                </td>
                <td className="py-3 text-right">
                  {isAdmin && u.id !== user.id ? (
                    <button onClick={() => toggleActive(u)} data-testid={`user-toggle-${u.id}`}
                      className={`badge-pill ${u.is_active ? "badge-approved" : "badge-rejected"} cursor-pointer hover:opacity-80`}>
                      {u.is_active ? t("settings.active") : t("settings.inactive")}
                    </button>
                  ) : (
                    <span className={`badge-pill ${u.is_active ? "badge-approved" : "badge-rejected"}`}>{u.is_active ? t("settings.active") : t("settings.inactive")}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Param({ label, value, tone }) {
  const toneClass = tone === "red" ? "text-[#F87171]" : "text-[#F8FAFC]";
  return (
    <div className="border-l-2 border-l-[#1E293B] pl-3">
      <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{label.toUpperCase()}</div>
      <div className={`text-sm ${toneClass}`}>{value}</div>
    </div>
  );
}

function HolidayRotationSection({ users, canEdit, t }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const owners = users.filter(u => u.role === "owner_director");

  const reload = async (y = year) => {
    setLoading(true);
    try {
      const { data } = await api.get("/holiday-rotations", { params: { year: y } });
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(year); /* eslint-disable-next-line */ }, [year]);

  const patch = async (id, updates) => {
    setSavingId(id);
    try {
      const { data } = await api.patch(`/holiday-rotations/${id}`, updates);
      setRows(rs => rs.map(r => (r.id === id ? data : r)));
      toast.success(t("holidays.saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSavingId(null);
    }
  };

  const ownerName = (id) => users.find(u => u.id === id)?.name || "—";
  const ownerColor = (id) => {
    // Stable color per owner based on sorted index
    const sorted = [...owners].sort((a, b) => a.email.localeCompare(b.email));
    const idx = sorted.findIndex(o => o.id === id);
    if (idx === 0) return "bg-[#007AFF]/15 border-[#007AFF]/40 text-[#4DA6FF]";
    if (idx === 1) return "bg-[#10B981]/15 border-[#10B981]/40 text-[#34D399]";
    return "bg-[#0B1221] border-[#1E293B] text-[#F8FAFC]";
  };

  return (
    <div className="ledger-card p-5" data-testid="holidays-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-4 h-4 text-[#FBBF24]" />
          <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("holidays.subtitle")}</div>
        </div>
        <div className="flex items-center gap-1" data-testid="holidays-year-nav">
          <button
            onClick={() => setYear(y => y - 1)} data-testid="holidays-year-prev"
            className="p-1.5 rounded-md bg-[#050A14] border border-[#1E293B] hover:border-[#4DA6FF] text-[#94A3B8] hover:text-[#F8FAFC]"
          ><ChevronLeft className="w-3 h-3" /></button>
          <div className="px-4 py-1 rounded-md bg-[#050A14] border border-[#1E293B] font-mono text-sm min-w-[70px] text-center" data-testid="holidays-year">{year}</div>
          <button
            onClick={() => setYear(y => y + 1)} data-testid="holidays-year-next"
            className="p-1.5 rounded-md bg-[#050A14] border border-[#1E293B] hover:border-[#4DA6FF] text-[#94A3B8] hover:text-[#F8FAFC]"
          ><ChevronRight className="w-3 h-3" /></button>
        </div>
      </div>

      <p className="text-xs text-[#94A3B8] mb-4">{t("holidays.intro")}</p>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-10 rounded-md bg-[#050A14] animate-pulse" />)}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-[#94A3B8] font-mono italic">{t("holidays.empty")}</div>
      ) : (
        <div className="space-y-2">
          {rows.map(r => (
            <div key={r.id}
              data-testid={`holiday-row-${r.holiday_key}`}
              className="grid grid-cols-1 md:grid-cols-[1.3fr_auto_auto_1fr] items-center gap-2 md:gap-3 p-3 rounded-md bg-[#050A14] border border-[#1E293B]">
              <div>
                <div className="text-sm font-medium">{t(`holidays.names.${r.holiday_key}`) || r.title}</div>
                <div className="font-mono text-[10px] text-[#94A3B8]">{r.holiday_key}</div>
              </div>
              <Input
                type="date" value={r.start_date} disabled={!canEdit || savingId === r.id}
                onChange={(e) => patch(r.id, { start_date: e.target.value })}
                data-testid={`holiday-start-${r.holiday_key}`}
                className="bg-[#0B1221] border-[#1E293B] w-36 text-xs"
              />
              <Input
                type="date" value={r.end_date} disabled={!canEdit || savingId === r.id}
                onChange={(e) => patch(r.id, { end_date: e.target.value })}
                data-testid={`holiday-end-${r.holiday_key}`}
                className="bg-[#0B1221] border-[#1E293B] w-36 text-xs"
              />
              {canEdit ? (
                <select
                  value={r.assigned_owner_id || ""} disabled={savingId === r.id}
                  onChange={(e) => patch(r.id, { assigned_owner_id: e.target.value })}
                  data-testid={`holiday-owner-${r.holiday_key}`}
                  className={`rounded-md px-2 py-2 text-xs border ${ownerColor(r.assigned_owner_id)}`}
                >
                  <option value="">—</option>
                  {owners.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              ) : (
                <span className={`badge-pill border ${ownerColor(r.assigned_owner_id)}`}>
                  {ownerName(r.assigned_owner_id)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
